/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey
 */

import { Injectable, Injector, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { UUID } from 'aws-sdk/clients/inspector';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { Logger } from '../logger/logger.service';
import { delay, switchMap } from 'rxjs/operators';
import { OrganisationCognitoUser } from '@app/models/cognito-organisation-user.model';
import { OrganisationUserService } from '../api-services/organisation-users/organisation-user.service';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
const log = new Logger('UserListService');
import * as AWS from '@base/node_modules/aws-sdk';
import { ImageParserService } from '../shared-services/image-parser.service';
import { AuthenticationService } from '../authentication/authentication.service';

const poolData = {
  UserPoolId: environment.cognitoSettings.userPoolId,
  ClientId: environment.cognitoSettings.clientId,
};
const userPool = new CognitoUserPool(poolData);

/**
 *  model for one single user from an AWS API call - can't find it in the API
 *
 * @export
 * @class User
 */
export interface CognitoUser {
  Attributes: UserAttributes[];
  Enabled: boolean;
  UserCreateDate: Date;
  UserLastModifiedDate: Date;
  UserStatus: string;
  Username: string;
}
/**
 *  Model to determine how user attributes can be fetched
 * Attributes can be - .sub, .email_verified, .name, family_name, .email
 *
 * @export
 * @class UserAttributes
 */
export interface UserAttributes {
  Name: string;
  Value: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserListService implements OnDestroy {
  // Typed as potentially any as it could be an unstructured HTTP Response
  private organisationUsers: any[] | OrganisationUsersModel[] = [];
  public _userList: OrganisationCognitoUser[] = [];
  public dataSubject = new Subject<OrganisationCognitoUser[]>();
  public lastFetch: Date;

  // Subscriptions
  private userListSubscription: Subscription;
  private delaySubscription: Subscription;
  private organisationUserSubscription: Subscription;
  private logoutSubscription: Subscription;

  constructor(
    private organisationUserService: OrganisationUserService,
    private injector: Injector,
    private auth: AuthenticationService
  ) {}

  get userList(): OrganisationCognitoUser[] {
    return this._userList;
  }

  set userList(value: OrganisationCognitoUser[]) {
    this._userList = value;
  }
  /**
   *  Ryan Duffy
   *
   *  getUserNickname
   *
   *  get a user's nickname from the list based on user ID
   *
   * @param userid: UUID
   *
   * @returns void get the user that matches the id
   */
  getUserNickname(userid: UUID): Observable<any> {
    let cognitoIdentityServiceProvider: any;
    let params: any;
    return new Observable((observer) => {
      AWS.config.region = environment.cognitoSettings.region; // Region
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoSettings.identityPoolId,
      });
      cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();
      params = {
        UserPoolId: environment.cognitoSettings.userPoolId,
        AttributesToGet: ['name'],
        Filter: `sub = "${userid}"`,
      };

      cognitoIdentityServiceProvider.listUsers(params, (err, data) => {
        if (err) {
          observer.error(err);
          observer.complete();
        } else {
          observer.next(data);
          observer.complete();
        }
      });
    });
  }
  /**
   *  Ryan Duffy
   *
   *  getAllCognitoUsers
   *
   *  get a list of the current organisation's user from the cognito userpool
   *
   * @returns void get the users in an observable which we can subscribe to
   */
  getAllCognitoUsers(): Observable<any> {
    const parser = this.injector.get(ImageParserService);
    let cognitoIdentityServiceProvider: any;
    let params: any;
    let userList: CognitoUser[];
    let fullUserList: OrganisationCognitoUser[] = [];
    let fullUser: OrganisationCognitoUser;
    let orgUser: OrganisationUsersModel;
    const displayUrls: string[] = [];
    return new Observable((observer) => {
      AWS.config.region = environment.cognitoSettings.region;
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoSettings.identityPoolId,
      });
      // Need to revisit a more secure way to do this
      AWS.config.update({
        credentials: this.auth.accessCredentials,
      });
      cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();
      params = {
        UserPoolId: environment.cognitoSettings.userPoolId,
      };
      cognitoIdentityServiceProvider.listUsers(
        params,
        (err, data) => {
          if (err) {
            observer.error(err);
            observer.complete();
          } else {
            userList = data.Users;
            for (const cognitoUser of userList) {
              fullUser = new OrganisationCognitoUser();
              fullUser.username = cognitoUser.Username;
              fullUser.userEnabled = cognitoUser.Enabled;
              fullUser.userLastModifiedDate = cognitoUser.UserLastModifiedDate;
              fullUser.userStatus = cognitoUser.UserStatus;
              fullUser.userCreateDate = cognitoUser.UserCreateDate;
              fullUser.organisationId = environment.product_owner_uuid;
              for (const attribute of cognitoUser.Attributes) {
                switch (attribute.Name) {
                  case 'sub':
                    fullUser.userId = attribute.Value;
                    break;
                  case 'name':
                    fullUser.firstName = attribute.Value;
                    break;
                  case 'family_name':
                    fullUser.familyName = attribute.Value;
                    break;
                  case 'phone_number':
                    fullUser.phoneNumber = attribute.Value;
                    break;
                  case 'email':
                    fullUser.email = attribute.Value;
                    break;
                  case 'custom:favourite_color':
                    fullUser.favouriteColour = attribute.Value;
                    break;
                  case 'custom:display_picture':
                    fullUser.displayPicture = attribute.Value;
                    break;
                }
              }
              fullUserList.push(fullUser);
            }
            fullUserList.forEach((user) => {
              displayUrls.push(user.displayPicture);
            });
            this.organisationUserSubscription = parser
              .parseArrayUrls(fullUserList)
              .pipe(
                switchMap((users) => {
                  fullUserList = users;
                  return this.organisationUserService.getOrganisationUsersByOrganisationId(environment.product_owner_uuid);
                })
              )
              .subscribe((users) => {
                if (!Array.isArray(users)) {
                  users = [users];
                }
                this.organisationUsers = users;
                for (const user of this.organisationUsers) {
                  if (user instanceof OrganisationUsersModel) {
                    orgUser = user;
                  } else {
                    orgUser = new OrganisationUsersModel().fromJson(user);
                  }
                  fullUser =
                    fullUserList.find(
                      (fullCognitoUser) => fullCognitoUser.userId === orgUser.userId
                    ) || undefined;
                  if (fullUser) {
                    fullUser.userLastLogin = orgUser.sessionTimestamp;
                    fullUser.organisationRoleId = orgUser.roleId;
                  }
                }
                observer.next(fullUserList);
                observer.complete();
                if (this.organisationUserSubscription) {
                  this.organisationUserSubscription.unsubscribe();
                }
              });
          }
        },
        (err) => {
          log.error(err);
        }
      );
    });
  }
  /**
   *  Ryan Duffy
   *
   *  getUserListData
   *
   *  Pass the userlistdata to the subject
   */
  getUserListData() {
    this.userListSubscription = this.getAllCognitoUsers().subscribe(
      (data: OrganisationCognitoUser[]) => {
        this.handleUserListData(data);
      }
    );
  }
  /**
   *  Ryan Duffy
   *
   *  handleUserListData
   *
   *  get the userlistdata and pass it into the locally stored list
   *
   * @todo sort out the local list - not sure what I am doing overly wrong
   */
  handleUserListData(data: OrganisationCognitoUser[]) {
    this.userList = [];
    this.lastFetch = new Date();
    this.dataSubject.next(data);
    for (const user of data) {
      this.userList.push(user);
    }
  }
  /**
   *  passes the localList to the datasubject OR the API if not available and
   * checks the time to ensure that the list is updated frequently
   *
   * @memberof UserListService
   */
  getUserListFromLocalList() {
    const localUserList = this.userList;
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (localUserList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.lastFetch ? this.lastFetch.getTime() : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(localUserList)
        .pipe(delay(0))
        .subscribe((userList) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches > 60) {
            this.dataSubject.next(userList);
            this.getUserListData();
          } else {
            this.dataSubject.next(userList);
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.delaySubscription = of(null)
        .pipe(delay(0))
        .subscribe(() => {
          this.getUserListData();
        });
    }
  }
  /**
   *  passes the localList to the datasubject OR the API if not available and
   * checks the time to ensure that the list is updated frequently
   *
   * @memberof UserListService
   */
  getUserListFromLocalListBySetTime(time) {
    const localUserList = this.userList;
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (localUserList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.lastFetch ? this.lastFetch.getTime() : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(localUserList)
        .pipe(delay(0))
        .subscribe((userList) => {
          // If the time difference betweem the current time and the last fetch is more than or equal to set time
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches >= time) {
            this.dataSubject.next(userList);
            this.getUserListData();
          } else {
            this.dataSubject.next(userList);
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.delaySubscription = of(null)
        .pipe(delay(0))
        .subscribe(() => {
          this.getUserListData();
        });
    }
  }
  /**
   *  gets the user from the list by their userId if it exists
   *
   * @param {string} userId
   * @returns {*}
   * @memberof UserListService
   */
  getUserByUserId(userId: string): OrganisationCognitoUser {
    if (this.userList) {
      for (const user of this.userList) {
        if (user.userId === userId) {
          return user;
        }
      }
    }
  }

  getUserFullNameById(userId:string): string {
    if (this.userList) {
      for (const user of this.userList) {
        if (user.userId === userId) {
          return user.firstName + ' ' + user.familyName;
        }
      }
    }
  }

  updateInList(data: OrganisationCognitoUser): void {
    let index = 0;
    for (let user of this.userList) {
      if (user.userId === data.userId) {
        this.userList.splice(index, 1);
        this.userList.push(data);
      }
      index++;
    }
  }

  removeFromList(data: OrganisationCognitoUser) {
    const index = this.userList.findIndex((user) => user.userId === data.userId);
    this.userList.splice(index, 1);
  }

  ngOnDestroy() {
    if (this.delaySubscription) {
      this.delaySubscription.unsubscribe();
    }
    if (this.userListSubscription) {
      this.userListSubscription.unsubscribe();
    }
    if (this.organisationUserSubscription) {
      this.organisationUserSubscription.unsubscribe();
    }
  }
}
